import moment from "moment";
import api from "../api";
import {
  RECORDING_LIST_REQUEST,
  RECORDING_LIST_SUCCESS,
  RECORDING_LIST_FAILURE,
  RECORDING_ADD_LIST,
  RECORDING_UPDATE_REQUEST,
  RECORDING_UPDATE_SUCCESS,
  RECORDING_UPDATE_FAILURE,
  RECORDING_BULK_UPDATE_REQUEST,
  RECORDING_BULK_UPDATE_SUCCESS,
  RECORDING_BULK_UPDATE_FAILURE,
  RECORDING_REVIEW_REQUEST,
  RECORDING_REVIEW_SUCCESS,
  RECORDING_REVIEW_FAILURE,
} from "./types";
import { projectUpdateRecordedDuration } from "./projectActions";
import { timeToStale } from "../constants";
import { commentList } from "./commentAction";
import { notificationDisplay } from "./notificationActions";

const DEFAULT_ERROR = "Recording endpoint error";

// ---------------- LIST RECORDINGS ----------------
const recordingListRequest = (projectId) => ({
  type: RECORDING_LIST_REQUEST,
  projectId,
});

const recordingListSuccess = (recordings, projectId) => ({
  type: RECORDING_LIST_SUCCESS,
  recordings,
  projectId,
});

const recordingListFailure = (projectId) => ({
  type: RECORDING_LIST_FAILURE,
  projectId,
});

export const recordingList = (projectId) => async (dispatch, getState) => {
  // check cache
  const cachedAt = getState().recordings.cachedAt[projectId];
  if (cachedAt && moment().diff(cachedAt, "m") < timeToStale) return;
  // cache got stale
  dispatch(recordingListRequest(projectId));
  let resp, status, detail;
  try {
    resp = await api.recorder.recording.list(projectId);
    status = resp.status;
  } catch (error) {
    console.log(RECORDING_LIST_FAILURE, error);
    dispatch(recordingListFailure(DEFAULT_ERROR));
  }
  switch (status) {
    case 200: {
      const recordings = await resp.json();
      dispatch(recordingListSuccess(recordings, projectId));
      dispatch(commentList(projectId));
      break;
    }
    case 403:
      detail = resp.json().detail;
      dispatch(recordingListFailure(detail));
      dispatch(notificationDisplay("error", [detail]));
      break;
    case 422:
      detail = resp.json().detail;
      dispatch(recordingListFailure(DEFAULT_ERROR));
      dispatch(
        notificationDisplay(
          "error",
          detail.map((d) => `${d.loc[d.loc.length - 1]}: ${d.msg}`)
        )
      );
      break;
    default:
      console.log(RECORDING_LIST_FAILURE, status);
      dispatch(recordingListFailure(DEFAULT_ERROR));
      dispatch(notificationDisplay("error", [DEFAULT_ERROR]));
      break;
  }
};

// ---------------- ADD LIST OF RECORDINGS (UPLOAD DATASET) ----------------
export const recordingAddList = (recordings, projectId) => ({
  type: RECORDING_ADD_LIST,
  recordings,
  projectId,
});

// ---------------- UPDATE RECORDING  ----------------
const recordingUpdateRequest = () => ({
  type: RECORDING_UPDATE_REQUEST,
});

const recordingUpdateSuccess = (recording) => ({
  type: RECORDING_UPDATE_SUCCESS,
  recording,
});

const recordingUpdateFailure = (errors) => ({
  type: RECORDING_UPDATE_FAILURE,
  errors,
});

export const recordingUpdate = (recording, userFile = null) => async (
  dispatch
) => {
  dispatch(recordingUpdateRequest());
  let resp, status, detail;
  try {
    resp = await api.recorder.recording.update(recording, userFile);
    status = resp.status;
  } catch (error) {
    console.log(RECORDING_UPDATE_FAILURE, error);
    dispatch(recordingUpdateFailure(DEFAULT_ERROR));
  }
  switch (status) {
    case 200: {
      const updatedRec = await resp.json();
      dispatch(recordingUpdateSuccess(updatedRec));
      if (userFile) {
        dispatch(
          projectUpdateRecordedDuration(
            recording.project_id,
            recording.duration
          )
        );
      }
      break;
    }
    case 403:
    case 500:
      detail = resp.json().detail;
      dispatch(recordingUpdateFailure(detail));
      dispatch(notificationDisplay("error", [detail]));
      break;
    case 422:
      detail = resp.json().detail;
      dispatch(recordingUpdateFailure(detail));
      dispatch(
        notificationDisplay(
          "error",
          detail.map((d) => `${d.loc[d.loc.length - 1]}: ${d.msg}`)
        )
      );
      break;
    default:
      console.log(RECORDING_UPDATE_FAILURE, status);
      dispatch(recordingUpdateFailure(DEFAULT_ERROR));
      dispatch(notificationDisplay("error", [DEFAULT_ERROR]));
      break;
  }
};

// ---------------- BULK UPDATE RECORDING  ----------------
const recordingBulkUpdateRequest = (projectId) => ({
  type: RECORDING_BULK_UPDATE_REQUEST,
  projectId,
});

const recordingBulkUpdateSuccess = (projectId, recordings, comments) => ({
  type: RECORDING_BULK_UPDATE_SUCCESS,
  projectId,
  recordings,
  comments,
});

const recordingBulkUpdateFailure = (projectId, error) => ({
  type: RECORDING_BULK_UPDATE_FAILURE,
  projectId,
  error,
});

export const recordingBulkUpdate = (projectId, file) => async (dispatch) => {
  dispatch(recordingBulkUpdateRequest(projectId));
  let resp, status, detail;
  try {
    resp = await api.recorder.recording.bulkUpdate(projectId, file);
    status = resp.status;
  } catch (error) {
    console.log(RECORDING_BULK_UPDATE_FAILURE, error);
    dispatch(recordingBulkUpdateFailure(DEFAULT_ERROR));
  }
  switch (status) {
    case 200: {
      const respData = await resp.json();
      dispatch(
        recordingBulkUpdateSuccess(
          projectId,
          respData.recordings,
          respData.comments
        )
      );
      break;
    }
    case 403:
      detail = resp.json().detail;
      dispatch(recordingBulkUpdateFailure(projectId));
      dispatch(notificationDisplay("error", [detail]));
      break;
    case 422:
      detail = resp.json().detail;
      dispatch(recordingBulkUpdateFailure(projectId));
      dispatch(
        notificationDisplay(
          "error",
          detail.map((d) => `${d.loc[d.loc.length - 1]}: ${d.msg}`)
        )
      );
      break;
    default:
      console.log(RECORDING_BULK_UPDATE_FAILURE, status);
      dispatch(recordingBulkUpdateFailure(projectId));
      dispatch(notificationDisplay("error", [DEFAULT_ERROR]));
      break;
  }
};

// ---------------- REVIEW RECORDING ----------------
const recordingReviewRequest = () => ({
  type: RECORDING_REVIEW_REQUEST,
});

const recordingReviewSuccess = (recording) => ({
  type: RECORDING_REVIEW_SUCCESS,
  recording,
});

const recordingReviewFailure = (error) => ({
  type: RECORDING_REVIEW_FAILURE,
  error,
});

export const recordingReview = (recordingId, reviewer_status) => async (
  dispatch
) => {
  dispatch(recordingReviewRequest());
  let resp, status, detail;
  try {
    resp = await api.recorder.recording.review(recordingId, reviewer_status);
    status = resp.status;
  } catch (error) {
    console.log(RECORDING_REVIEW_FAILURE, error);
    dispatch(recordingBulkUpdateFailure(DEFAULT_ERROR));
  }
  switch (status) {
    case 200: {
      const recording = await resp.json();
      dispatch(recordingReviewSuccess(recording));
      break;
    }
    case 403:
      detail = resp.json().detail;
      dispatch(recordingReviewFailure(detail));
      dispatch(notificationDisplay("error", [detail]));
      break;
    case 422:
      detail = resp.json().detail;
      dispatch(recordingReviewFailure(detail));
      dispatch(
        notificationDisplay(
          "error",
          detail.map((d) => `${d.loc[d.loc.length - 1]}: ${d.msg}`)
        )
      );
      break;
    default:
      console.log(RECORDING_REVIEW_FAILURE, status);
      dispatch(recordingBulkUpdateFailure(recordingId));
      dispatch(notificationDisplay("error", [DEFAULT_ERROR]));
      break;
  }
};
